import dayjs from '../../utils/dayjs';

export function normalizeInviteEmail(email) {
  return String(email ?? '')
    .trim()
    .toLowerCase();
}

export function getInviteEmail(record) {
  return String(record?.email || record?.assignedTo || record?.assignEmail || '').trim();
}

export function resolveUploadId(record) {
  if (!record) return null;
  const raw = record.raw || record;
  const candidate =
    record.uploadId ??
    record.uploadID ??
    record.inviteeId ??
    record.userId ??
    raw.uploadId ??
    raw.uploadID ??
    raw.id ??
    raw.userId ??
    null;
  return candidate != null && candidate !== '' ? String(candidate) : null;
}

const SENT_STATUS_TOKENS = new Set(['sent', 'invitesent', 'invitationsent', 'inviteemailsent', 'emailsent']);
const AWAITING_INVITATION_TOKENS = new Set(['awaitinginvitation', 'awaitinginvite', 'uploadsucceeded', 'uploaded']);

/** Backend UserInvitationStatus — UploadSucceeded = 1. */
export const INVITE_USER_STATUS = {
  UploadSucceeded: 1,
};

export function isAwaitingInvitationStatus(statusOrRecord) {
  const status =
    statusOrRecord != null && typeof statusOrRecord === 'object'
      ? getInviteeBackendStatus(statusOrRecord)
      : statusOrRecord;
  if (status === INVITE_USER_STATUS.UploadSucceeded || status === '1') return true;
  const token = normalizeStatusToken(status);
  return Boolean(token) && AWAITING_INVITATION_TOKENS.has(token);
}

export function isUploadSucceededStatus(statusOrRecord) {
  return isAwaitingInvitationStatus(statusOrRecord);
}

function normalizeStatusToken(value) {
  if (value == null || value === '') return '';
  return String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');
}

function isSentStatusToken(token) {
  return Boolean(token) && SENT_STATUS_TOKENS.has(token);
}

function pickField(raw, ...keys) {
  for (let i = 0; i < keys.length; i += 1) {
    const value = raw?.[keys[i]];
    if (value != null && String(value).trim() !== '') return String(value).trim();
  }
  return '';
}

function formatDisplayDate(value) {
  if (!value) return '';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : String(value);
}

/** Raw status string from GET /onboarding/orgusers/invitees (e.g. Uploaded, InvitationSent). */
export function getInviteeBackendStatus(record) {
  const raw = record?.raw || record || {};
  return pickField(raw, 'status', 'inviteStatus', 'invitationStatus') || pickField(record, 'status', 'inviteStatus');
}

export function formatInviteeStatusLabel(status) {
  if (isAwaitingInvitationStatus(status)) return 'Awaiting invitation';

  const value = String(status ?? '').trim();
  if (!value) return '—';

  const labels = {
    Uploaded: 'Awaiting invitation',
    UploadSucceeded: 'Awaiting invitation',
    AwaitingInvitation: 'Awaiting invitation',
    InvitationSent: 'Invitation sent',
    InviteSent: 'Invitation sent',
    Revoked: 'Revoked',
  };

  return labels[value] || value.replace(/([a-z])([A-Z])/g, '$1 $2');
}

function mapPendingInviteFields(raw = {}) {
  const source = raw || {};
  const fileName = pickField(source, 'fileName', 'filename', 'sourceFileName');
  const uploadDateRaw = pickField(source, 'uploadDate', 'uploadedDate');
  const uploadDate = formatDisplayDate(uploadDateRaw);
  const fileDisplay = fileName && uploadDate ? `${fileName} (${uploadDate})` : fileName || uploadDate || '';
  const backendStatus = pickField(source, 'status', 'inviteStatus', 'invitationStatus');

  const lastUpdatedRaw = pickField(source, 'lastUpdatedDate', 'lastUpdated', 'updatedDate', 'modifiedDate');
  const lastUpdatedParsed = lastUpdatedRaw ? dayjs(lastUpdatedRaw) : null;

  return {
    firstName: pickField(source, 'firstName'),
    lastName: pickField(source, 'lastName'),
    businessName: pickField(source, 'fullBusinessname', 'fullBusinessName', 'businessName'),
    organisationName: pickField(source, 'organisationName', 'orgName'),
    tradingName: pickField(source, 'tradingName'),
    businessRegistrationNumber: pickField(source, 'businessRegistrationNumber', 'registrationNumber'),
    planName: pickField(source, 'planName'),
    currency: pickField(source, 'currency'),
    status: backendStatus,
    statusLabel: formatInviteeStatusLabel(backendStatus),
    lastUpdatedDate: lastUpdatedParsed?.isValid() ? lastUpdatedParsed.format('DD/MM/YYYY') : lastUpdatedRaw,
    lastUpdatedTimestamp: lastUpdatedParsed?.isValid() ? lastUpdatedParsed.valueOf() : 0,
    isRevoked: isRevokedInvitee(source),
    sourceFileName: fileName,
    uploadDate,
    fileDisplay,
    uploadError: pickField(source, 'uploadError', 'errorMessage', 'importError'),
  };
}

/**
 * Resolves invite badge from API fields on subscription or invitee rows.
 * UserInvitees: status (UploadSucceeded | Uploaded | InvitationSent | Revoked).
 */
export function resolveInviteStatus(record) {
  const raw = record?.raw || record || {};

  const inviteStatusToken = normalizeStatusToken(raw.inviteStatus || raw.invitationStatus || record.inviteStatus);
  if (isSentStatusToken(inviteStatusToken)) return 'sent';

  const statusToken = normalizeStatusToken(raw.status || record.status);
  if (isSentStatusToken(statusToken)) return 'sent';

  if (raw.inviteSent || raw.invitationSent || raw.inviteEmailSent) return 'sent';
  if (record?.inviteSentAt || raw.inviteSentAt) return 'sent';

  return 'pending';
}

export function isInvitationSentStatus(record) {
  return resolveInviteStatus(record) === 'sent';
}

export function getSendInviteButtonLabel(record) {
  return isInvitationSentStatus(record) ? 'Re-send invite' : 'Send Invite';
}

export function isRevokedInvitee(invitee) {
  const statusToken = normalizeStatusToken(invitee?.status || invitee?.inviteStatus);
  return statusToken === 'revoked' || Boolean(invitee?.isRevoked);
}

export function isStagedInviteeRow(record) {
  if (!record || hasSubscriptionId(record)) return false;
  return Boolean(record.inviteeId || record.isOnboardingInvitee || record.raw?.id);
}

export function getInviteeRecordId(record) {
  const id = record?.inviteeId ?? record?.raw?.id ?? resolveUploadId(record);
  return id != null && id !== '' ? String(id) : null;
}

function findInviteeByEmail(invitees, email) {
  const normalized = normalizeInviteEmail(email);
  if (!normalized || !Array.isArray(invitees)) return null;
  return invitees.find((invitee) => normalizeInviteEmail(getInviteEmail(invitee)) === normalized) || null;
}

/**
 * Resolves invitee payload for PUT /onboarding/orgusers/invitees/{id}.
 */
export function resolveEditableAssignmentRecord(record, invitees = []) {
  if (!record) return null;

  if (record.inviteeRaw?.id != null) {
    return normalizeInviteePendingRow(record.inviteeRaw);
  }

  if (record.isOnboardingInvitee && getInviteeRecordId(record)) {
    return record;
  }

  const matchedInvitee = findInviteeByEmail(invitees, getInviteEmail(record));
  if (matchedInvitee?.id != null) {
    return normalizeInviteePendingRow(matchedInvitee);
  }

  return null;
}

/** True when PUT /onboarding/orgusers/invitees/{id} can be called for this row. */
export function canEditAssignment(record, invitees = []) {
  return Boolean(getInviteeRecordId(resolveEditableAssignmentRecord(record, invitees)));
}

export function hasSubscriptionId(record) {
  const raw = record?.raw || record;
  return Boolean(record?.subscriptionId ?? raw?.subscriptionId);
}

/** Rows eligible for bulk send/resend selection. */
export function isInvitableRow(record) {
  const email = getInviteEmail(record);
  if (!email) return false;
  if (isRevokedInvitee(record?.raw || record)) return false;
  return true;
}

export function getPendingRowKey(record) {
  return (
    record?.key ||
    record?.userId ||
    resolveUploadId(record) ||
    record?.inviteeId ||
    record?.subscriptionId ||
    record?.subscriptionKey ||
    getInviteEmail(record)
  );
}

export function getAllocatableCapacity(summary) {
  if (!summary) return 0;
  const unAssigned = summary.unAssigned ?? summary.remaining ?? 0;
  const unusedPromo = summary.unusedPromo ?? summary.sent ?? summary.unUsedPromoCodes ?? 0;
  return Math.max(0, unAssigned + unusedPromo);
}

export function normalizeSubscriptionPendingRow(item, linkedInvitee = null) {
  const email = getInviteEmail(item);
  if (!email) return null;

  const inviteeId = linkedInvitee?.id != null ? String(linkedInvitee.id) : null;
  const inviteeFields = linkedInvitee ? mapPendingInviteFields(linkedInvitee) : mapPendingInviteFields(item);
  const backendStatus = inviteeFields.status || pickField(item, 'status', 'subscriptionStatus');

  return {
    ...item,
    ...inviteeFields,
    key: `subscription-${item.subscriptionId || item.key || item.subscriptionKey || email}`,
    email,
    assignedTo: email,
    organisationName: inviteeFields.organisationName || item.organisationName || inviteeFields.businessName || '',
    planName: inviteeFields.planName || item.planName || '',
    status: backendStatus,
    statusLabel: formatInviteeStatusLabel(backendStatus),
    assignedDate: item.assignedDate || item.activeDate || inviteeFields.uploadDate || null,
    brokerName: item.brokerName || '',
    inviteStatus: resolveInviteStatus({ ...item, status: backendStatus }),
    uploadId: inviteeId || resolveUploadId(item),
    subscriptionId: item.subscriptionId ?? null,
    inviteeId,
    inviteeRaw: linkedInvitee || null,
    isOnboardingInvitee: Boolean(linkedInvitee),
    raw: linkedInvitee || item,
  };
}

export function normalizeInviteePendingRow(invitee) {
  const email = getInviteEmail(invitee);
  if (!email) return null;

  const uploadId = invitee.id != null ? String(invitee.id) : resolveUploadId(invitee);
  const fields = mapPendingInviteFields(invitee);
  const roleName = invitee.roleName || null;

  return {
    key: `invitee-${invitee.id || email}`,
    email,
    assignedTo: email,
    assignName: [fields.firstName, fields.lastName].filter(Boolean).join(' ').trim() || fields.firstName || null,
    ...fields,
    organisationName: fields.organisationName || fields.businessName || fields.tradingName || '',
    assignedDate: fields.uploadDate || null,
    activeDate: fields.uploadDate || null,
    brokerName: roleName,
    roleName,
    inviteStatus: resolveInviteStatus(invitee),
    uploadId,
    subscriptionId: null,
    inviteeId: invitee.id != null ? String(invitee.id) : null,
    isOnboardingInvitee: true,
    raw: invitee,
  };
}

export function isPendingSubscriptionRow(item) {
  const email = getInviteEmail(item);
  return Boolean(email && !item.activeDate && !item.isRevoked);
}

/**
 * Merge subscription pending rows with staged invitees. De-dupe by email; subscription wins.
 */
export function mergePendingInviteRows(subscriptions = [], invitees = []) {
  const byEmail = new Map();
  const activeInvitees = (Array.isArray(invitees) ? invitees : []).filter((invitee) => !isRevokedInvitee(invitee));
  const inviteeByEmail = new Map();

  activeInvitees.forEach((invitee) => {
    const emailKey = normalizeInviteEmail(getInviteEmail(invitee));
    if (emailKey) inviteeByEmail.set(emailKey, invitee);
  });

  (Array.isArray(subscriptions) ? subscriptions : []).filter(isPendingSubscriptionRow).forEach((item) => {
    const emailKey = normalizeInviteEmail(getInviteEmail(item));
    const row = normalizeSubscriptionPendingRow(item, inviteeByEmail.get(emailKey));
    if (!row) return;
    byEmail.set(emailKey, row);
  });

  activeInvitees.forEach((invitee) => {
    const row = normalizeInviteePendingRow(invitee);
    if (!row) return;
    const emailKey = normalizeInviteEmail(row.email);
    if (byEmail.has(emailKey)) return;
    byEmail.set(emailKey, row);
  });

  return Array.from(byEmail.values());
}

export function isEmailAlreadyStaged(email, pendingRows = []) {
  const normalized = normalizeInviteEmail(email);
  if (!normalized) return false;
  return pendingRows.some((row) => normalizeInviteEmail(getInviteEmail(row)) === normalized);
}

/**
 * Capacity: re-sends are free; subscription-assigned rows are free; staged rows consume 1 each.
 */
export function partitionSendTargets(records, availableCapacity) {
  let remainingCapacity = availableCapacity;
  const sendable = [];
  const blocked = [];

  records.forEach((record) => {
    if (!isInvitableRow(record)) return;

    if (isInvitationSentStatus(record)) {
      sendable.push(record);
      return;
    }

    if (hasSubscriptionId(record)) {
      sendable.push(record);
      return;
    }

    if (remainingCapacity > 0) {
      sendable.push(record);
      remainingCapacity -= 1;
      return;
    }

    blocked.push(record);
  });

  return { sendable, blocked };
}

/**
 * Collect uploadIds for POST onboarding/orgusers/send-invitations (first send and re-send).
 */
export function partitionSendActions(records) {
  const uploadIds = [];
  const unsendable = [];

  records.forEach((record) => {
    const uploadId = resolveUploadId(record);
    if (uploadId) {
      uploadIds.push(String(uploadId));
      return;
    }

    unsendable.push(record);
  });

  return {
    uploadIds: [...new Set(uploadIds)],
    unsendable,
  };
}

export function canSelectInviteRow(record) {
  if (!isInvitableRow(record)) return false;
  if (isInvitationSentStatus(record)) return true;
  if (hasSubscriptionId(record)) return true;
  return Boolean(resolveUploadId(record));
}

export function canSendInviteRow(record, availableCapacity) {
  if (!canSelectInviteRow(record)) return false;
  if (isInvitationSentStatus(record)) return true;
  if (hasSubscriptionId(record)) return true;
  if (resolveUploadId(record)) return availableCapacity > 0;
  return false;
}

/** Staged or sent invitees with an id can be removed via DELETE invitees/{id}. */
export function canDeleteInviteRow(record) {
  if (isRevokedInvitee(record?.raw || record)) return false;
  return Boolean(getInviteeRecordId(record) ?? resolveUploadId(record));
}

export function getSendableInviteTargets(records, availableCapacity) {
  return partitionSendTargets(records, availableCapacity).sendable;
}
